import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Checkbox, Chip, CircularProgress, Collapse,
  Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../../animations/variants';
import {
  getIpamConfig, listIpamMembers, listIpamDiscoveredAccounts, addIpamMembers,
  retryIpamMember, removeIpamMember, listIpamShareInvitations,
  acceptIpamShareInvitation, startIpamRefresh,
} from '../../../../services/ipamApi';
import { listSecurityHubAccounts } from '../../../../services/securityhubApi';
import PortalPageHeader from '../../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalErrorState, PortalSectionHeader,
} from '../../../../components/portal/PortalStates';
import OperationProgress from '../OperationProgress';
import { POLL_INTERVAL_MS } from '../pollInterval';
import { formatStatusLabel } from '../../../../utils/statusLabel';
import {
  CK, FONT_BODY, sectionCardSx, insetPanelSx, tableHeadRowSx, headCellSx, rowSx,
  bodyCellSx, cidrCellSx, statusChipSx, dialogProps, dialogTitleSx,
} from '../../portalStyles';
import { IpamStateChip, IPAM_ISOLATED_SENTENCE, ipamStateIsIsolated } from './ipamState';
// IMPORTED, NEVER RETYPED. This sentence was hand-copied into the removal dialog
// below with a typographic apostrophe, which left the verification step — which
// greps for it verbatim — finding one occurrence out of four. The ASCII
// apostrophe in ipamCopy.js is deliberate; see the comment on the constant.
import {
  IPAM_ACCOUNT_DISCOVERY_UNREAD_HINT, IPAM_ACCOUNT_DISCOVERY_UNREAD_LABEL,
  IPAM_ACCOUNT_SWEEP_UNREAD_HINT,
  IPAM_LINKED_ACCOUNTS_UNREADABLE_BODY, IPAM_LINKED_ACCOUNTS_UNREADABLE_TITLE,
  IPAM_NO_VPC_DELETED,
  IPAM_REFRESH_NOT_CONFIGURED_REASON, IPAM_REFRESH_PENDING_REASON,
  IPAM_REFRESH_RUNNING_REASON, IPAM_REFRESH_UNREADABLE_REASON,
  IPAM_TENANT_NEVER_REFRESHED,
  ipamEnrollmentDisabledReason, ipamResidualIpamAlert,
} from './ipamCopy';

// Accounts — enrollment and discovery health, FUSED into one page.
//
// ── Why these are not two pages ──────────────────────────────────────────────
// Prism's enrollment phase says "we finished our work". AWS's
// GetIpamDiscoveredAccounts says "the sweep is actually running for this
// account". Neither is derivable from the other, and their DISAGREEMENT is the
// whole diagnostic: a cell parked at `enabled` whose discovery reports
// `not-found` is an account that is silently absent from every other table in
// the capability. Splitting the two axes would force an operator to correlate
// two pages to answer "why is this account missing from every table" — the single
// most likely question this capability generates.
//
// ── Three rules that decide whether this page tells the truth ────────────────
// 1. HEALTH IS JUDGED BY THE ABSENCE OF A FAILURE CODE. AWS returns NO code when
//    an account is healthy [SPIKE-VERIFIED] — there is no positive "ok" value to
//    read. Ranking a healthy cell "unknown" because no positive signal arrived is
//    the natural implementation and it paints a fully working org amber.
// 2. `LastSuccessfulDiscoveryTime` NIL ACROSS EVERY ROW MEANS "AWS HAS NEVER
//    COMPLETED A SWEEP", which must never render identically to "no resources
//    found". Those are opposite diagnoses with opposite next actions: wait, versus
//    go and fix something.
// 3. `ResourceDiscoveryStatus: 'not-found'` means the member's discovery was
//    deleted or its RAM share revoked. That account will NEVER appear, however
//    long anyone waits. It is not a slow case of `active`.
//
// And a fourth, structural one: the health feed being UNREADABLE is its own
// state. An account with no health row because the feed failed must not render
// the same as an account with no health row because AWS has not reached it yet.

const IPAM_COLOR = '#455A64';
const IPAM_COLOR_DARK = '#31434B';

// Prism's enrollment vocabulary — the eleven phases of IpamMemberStatus, which is
// a strict subset of the shared phaseWeight map plus this capability's two
// (`sharing`, `associating`).
//
// `disabled` is labelled "Removed", never "Disabled": the cell is a TOMBSTONE
// written only after the account's discovery and RAM share were torn down and its
// IAM grant detached, so it describes an account that is gone, not one that is
// present but switched off.
//
// `remove_failed` is deliberately NOT folded into `failed`. They need opposite
// remedies — `failed` means an enrollment failed and re-enrolling is right, while
// `remove_failed` means a teardown failed on an account the operator is trying to
// get rid of, and re-enrolling it re-attaches the grant they asked Prism to
// remove.
const PHASE_META = {
  pending: { intent: 'neutral', label: 'Pending', hint: 'The cell exists and no AWS call has been made for it yet.' },
  granting: { intent: 'info', label: 'Granting access', hint: 'Prism is attaching CKPrism-IPAM-Permissions to the account’s switch role and waiting for IAM to propagate. This runs for minutes and nothing is wrong.' },
  enabling: { intent: 'info', label: 'Creating discovery', hint: 'Prism is creating this account’s IPAM resource discovery.' },
  sharing: { intent: 'info', label: 'Sharing to host', hint: 'The member account is sharing its resource discovery to the IPAM host through AWS RAM.' },
  accepting: { intent: 'info', label: 'Accepting invitation', hint: 'The host is accepting the RAM invitation the member raised. This branch only happens in organizations without RAM organization sharing enabled.' },
  associating: { intent: 'info', label: 'Associating', hint: 'Prism is associating the member’s resource discovery with the IPAM. This is the longest step of an enable.' },
  removing: { intent: 'warning', label: 'Removing', hint: 'Prism is disassociating, un-sharing and deleting this account’s resource discovery.' },
  enabled: { intent: 'success', label: 'Enrolled', hint: 'Prism finished its work for this account. That is not the same as AWS having discovered it — read the discovery column.' },
  failed: { intent: 'error', label: 'Enrollment failed', hint: 'An enrollment step failed. Retry re-drives it, unless the failure is terminal.' },
  disabled: { intent: 'neutral', label: 'Removed', hint: 'This account was un-enrolled. The row is kept forever because it is the only record that Prism once attached an IAM policy to this account.' },
  remove_failed: { intent: 'error', label: 'Removal failed', hint: 'A teardown step failed on an account being removed. Re-enrolling it would re-attach the grant you asked Prism to remove, so Retry is deliberately not offered here.' },
};

const phaseMeta = (p) => PHASE_META[p]
  || { intent: 'neutral', label: formatStatusLabel(p) || 'Unknown', hint: 'Prism reported a phase this page does not recognise.' };

// Phases during which the backend is still working. `granting` belongs here: the
// grant gate waits for IAM propagation in every enrolled account, and a page that
// stops polling through it renders a frozen bar over a healthy operation.
const ACTIVE_PHASES = ['pending', 'granting', 'enabling', 'sharing', 'accepting', 'associating', 'removing'];

// AWS's ResourceDiscoveryStatus as Prism recorded it on the member cell. Two
// values, and `not-found` is NOT a variant of waiting.
const DISCOVERY_STATUS_META = {
  active: {
    intent: 'success',
    label: 'Discovery active',
    hint: 'AWS reports the association to this account’s resource discovery is active.',
  },
  'not-found': {
    intent: 'error',
    label: 'Discovery not found',
    hint: 'The member’s resource discovery was deleted, or its RAM share to the host was revoked. '
      + 'The association still exists and still consumes one of the IPAM’s association slots while '
      + 'nothing is being discovered. This account will never appear in the inventory tables, however '
      + 'long you wait — re-enrol it.',
  },
};

const discoveryStatusMeta = (s) => DISCOVERY_STATUS_META[s]
  || (s
    ? { intent: 'warning', label: formatStatusLabel(s), hint: 'AWS reported a discovery status Prism does not recognise. It is drawn as a warning rather than folded into either known value, because guessing here is how a revoked share ends up looking healthy.' }
    : null);

// AWS's FailureCode on GetIpamDiscoveredAccounts. THREE VALUES, THREE DIFFERENT
// REMEDIES — collapsing them into one "unhealthy" chip sends every operator down
// the wrong path two times in three.
const FAILURE_CODE_META = {
  'assume-role-failure': {
    intent: 'error',
    label: 'Assume-role failure',
    hint: 'AWS could not assume the role IPAM uses to read this account, so nothing in it is being '
      + 'discovered. This is Prism’s grant: run “Fix roles” on the Settings page, then re-check.',
  },
  'throttling-failure': {
    intent: 'warning',
    label: 'Throttled',
    hint: 'AWS throttled the discovery sweep for this account. This is transient and clears on its '
      + 'own — there is nothing to fix and retrying the enrollment will not help.',
  },
  'unauthorized-failure': {
    intent: 'error',
    label: 'Unauthorized',
    hint: 'AWS refused the discovery call for this account. This is normally a service control policy '
      + 'in the customer’s organization rather than anything Prism can change.',
  },
};

const failureCodeMeta = (c) => FAILURE_CODE_META[c]
  || { intent: 'error', label: formatStatusLabel(c), hint: 'AWS reported a failure code Prism does not recognise. Discovery is not running for this account and Region.' };

// The two sentences the BACKEND uses when it refuses a retry, spelled here
// VERBATIM rather than paraphrased.
//
// Both are terminal for reasons outside Prism's reach, and both are stamped into
// the cell's error_message by the enrollment fan-out — so the page can refuse the
// button up front instead of letting an operator watch the same failure land a
// fourth time. Paraphrasing them would mean the screen and the API disagree about
// why, which is the one thing an operator cannot check.
//
//   services/ipam_service.go:918   ipamIsolatedRefusal
//   services/ipam_service.go:1013  ipamQuotaGuidance
//
// The lower-case forms are the MATCHERS — they are compared against a
// lower-cased error_message, which arrives wrapped in Prism call context, so
// `includes` and not `===`. The sentence-cased forms are what goes on screen;
// the isolated one is the shared IPAM_ISOLATED_SENTENCE so the Accounts page and
// the two pool screens cannot word a departed account differently.
const IPAM_ISOLATED_REFUSAL = 'the AWS account that owns this resource has left the organization';
const IPAM_QUOTA_GUIDANCE = 'an IPAM service quota is exhausted; raise it under service code ec2-ipam and enrol the account again';
const IPAM_QUOTA_SENTENCE = 'An IPAM service quota is exhausted; raise it under service code ec2-ipam and enrol the account again.';

// Two independent sources of the same verdict, and the STRUCTURAL one is checked
// first: an isolate-* state is enumerated in the SDK and observable from any
// Describe call, while the error message is prose Prism has never actually seen
// produced (an isolate-* account requires removing an account from a live
// organization). The backend classifies this case structurally for exactly that
// reason and this page follows it rather than depending on the string alone.
const retryRefusal = (row) => {
  if (ipamStateIsIsolated(row?.associationState) || ipamStateIsIsolated(row?.discoveryState)) {
    return IPAM_ISOLATED_SENTENCE;
  }
  const m = String(row?.errorMessage || '').toLowerCase();
  if (m.includes(IPAM_ISOLATED_REFUSAL)) return IPAM_ISOLATED_SENTENCE;
  if (m.includes(IPAM_QUOTA_GUIDANCE)) return IPAM_QUOTA_SENTENCE;
  return null;
};

// RAM's ResourceShareInvitationStatus, verbatim. An EXPIRED invitation and a
// REJECTED one need different actions, so the four values are not reduced to a
// boolean and an unrecognised fifth is not absorbed into either.
const INVITATION_STATUS_META = {
  PENDING: { intent: 'warning', label: 'Pending', hint: 'The host has not accepted this share yet. Until it does, the member’s resource discovery cannot be associated with the IPAM.' },
  ACCEPTED: { intent: 'success', label: 'Accepted', hint: 'The host accepted this share. Nothing further is needed here.' },
  REJECTED: { intent: 'error', label: 'Rejected', hint: 'The host rejected this share. It cannot be accepted now — the member has to raise a new one, which happens when the account is re-enrolled.' },
  EXPIRED: { intent: 'error', label: 'Expired', hint: 'RAM expired this invitation before it was accepted. It cannot be accepted now — re-enrol the account so the member raises a fresh share.' },
};

const invitationStatusMeta = (s) => INVITATION_STATUS_META[String(s || '').toUpperCase()]
  || { intent: 'neutral', label: formatStatusLabel(s) || 'Unknown', hint: 'RAM reported an invitation status Prism does not recognise.' };

// Membership mutations are refused by the backend unless the config is settled,
// so every button that dispatches one is gated on the same condition or it
// produces a guaranteed 400.
const configAllowsMemberOps = (status) => status === 'enabled' || status === 'partial_failure';

// --- The "Re-check discovery" gate: allowed / refused / UNKNOWN --------------
//
// `disabled={busy || !config || config.status === 'disabled'}` had the same
// two-valued shape IpamPublicIPs.js carried, and the same hole in it: `!config`
// is "the read rejected", "the read has not landed" AND "the route answered its
// real 200 + data:null", and only the last of those is a reason to refuse the
// click. The other two were being spent as one, on a control that then sat there
// greyed out and unlabelled forever.
//
// The unreadable arm leaves the button live and lets the service answer — same
// argument as IpamPublicIPs.js, and it holds harder here because `run()` reloads
// the config after every mutation, so the click is also the retry for the read.
const discoveryRefreshGate = (config, configRead) => {
  if (config) {
    return config.status === 'disabled'
      ? { disabled: true, reason: IPAM_REFRESH_NOT_CONFIGURED_REASON }
      : { disabled: false, reason: 'Ask AWS for each enrolled account’s current discovery status.' };
  }
  if (configRead === 'ok') return { disabled: true, reason: IPAM_REFRESH_NOT_CONFIGURED_REASON };
  if (configRead === 'pending') return { disabled: true, reason: IPAM_REFRESH_PENDING_REASON };
  return { disabled: false, reason: IPAM_REFRESH_UNREADABLE_REASON };
};

// THE ONE MEMBER FILTER, shared verbatim with IpamDashboard.js and with the three
// inventory pages. `disabled` is a tombstone and `removing` is a departure
// already in flight; neither is an account whose coverage could be missing, so
// neither belongs in any count or denominator on this page.
//
// It is load-bearing rather than tidy. ListMembers returns tombstones on purpose
// ("TOMBSTONES ARE RETURNED, NOT FILTERED OUT"), removeMember tombstones by
// writing {"phase", "error_message"} and nothing else, and syncIpamLifecycleStates
// skips `phase = disabled` cells — so an account removed BECAUSE its discovery was
// not-found keeps `resource_discovery_status = 'not-found'` forever. Four pages
// used four different spellings of this filter and disagreed on screen about the
// same five accounts; this is the strictest of them, and now the only one.
const isCoveredMember = (r) => r?.phase !== 'disabled' && r?.phase !== 'removing';

// A poll that fails three times running is a real fault, not a blip — below that
// a background poll stays quiet rather than throwing a red card over an operation
// that is running perfectly well. Every other poll in this capability has this
// ceiling (IpamEntry.js, IpamDashboard.js, IpamAddressSpace.js, IpamAllocations.js,
// IpamPublicIPs.js, IpamBlockedPanel.js); this one had a bare `catch` and no
// counter, so a backend that started failing mid-enrol left a progress bar that
// never advanced and never errored while two requests went out every three
// seconds for as long as the tab stayed open.
const MAX_POLL_FAILURES = 3;

// Said on screen rather than left to a frozen bar. The operation may well still
// be running in AWS — Prism simply stopped being able to see it, and the bar
// below is the last state it could read, not a live one.
const POLL_STALLED_MESSAGE = 'Prism stopped polling for progress after three consecutive failures '
  + 'reading the enrollment state. Any operation below may still be running in AWS — the progress '
  + 'shown is the last state Prism could read, not a live one. Reload the page to resume polling.';

// --- Response reader --------------------------------------------------------
//
// /inventory/discovered-accounts answers ONE envelope, fixed by
// services.IpamInventoryPage and asserted by the backend's own tests:
//
//   {"items": [...], "nextCursor": ""}
//
// `items` is NEVER null on a successful read — the un-enabled tenant and the
// cold-start tenant both answer `{"items": [], "nextCursor": ""}`.
//
// This route is the ONE deliberate exception on the cursor: the health grid has
// no sort-key column, so it is never split and `nextCursor` is ALWAYS "". The key
// is still read rather than assumed, because reading it is what keeps the page
// cap below honest if that ever changes.
//
// The reader used to accept a bare array, then plausible key names, then any
// array-valued property. That was right while the route answered 501 and nothing
// pinned the wrapper; it is wrong now, because a reader that accepts five shapes
// cannot tell a renamed key from a normal response — and a silently empty health
// grid reads as "AWS has not reported anything yet", which is the precise
// confusion this page exists to prevent. A broken envelope now throws into
// loadHealth's catch, which sets healthError, which is a state this page already
// draws as a warning above the grid and as a blank-not-clean discovery column.
const ENVELOPE_ERROR = 'GetIpamDiscoveredAccounts answered in a shape Prism does not recognise. '
  + 'This is a defect, not an empty grid — the discovery column is blank because the feed could not '
  + 'be read, not because AWS reported nothing.';

const inventoryPage = (data) => {
  if (!data || typeof data !== 'object'
    || !Array.isArray(data.items) || typeof data.nextCursor !== 'string') {
    throw new Error(ENVELOPE_ERROR);
  }
  return data;
};

const toStringArray = (v) => {
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v || '[]'); } catch (_) { return []; }
};

const relativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

const absoluteTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
};

const plural = (n, one, many) => (n === 1 ? one : many);

const INVITATIONS_ANCHOR = 'ipam-share-invitations';

// One label/value row of the configuration card.
function ConfigRow({ label, value }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 2 }} sx={{ py: 0.5 }}>
      <Typography sx={{ minWidth: 170, fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY, fontSize: '0.875rem', fontWeight: 600,
          color: CK.navy, wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function IpamAccounts() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [config, setConfig] = useState(null);
  // 'pending' | 'ok' | 'failed' — the outcome of the config read, held as its own
  // value because `config === null` is the same null for three different facts
  // and `error` is shared with every mutation on this page. See
  // discoveryRefreshGate.
  const [configRead, setConfigRead] = useState('pending');
  const [members, setMembers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  // THE CANDIDATE LIST IS NOT A LABEL LOOKUP, AND ITS FAILURE IS NOT A ZERO.
  // `accounts` feeds `notEnrolled`, so a swallowed rejection here rendered "Every
  // linked account is enrolled — Nothing left to add" out of a read that never
  // returned, with no error anywhere and a select-all checkbox disabled for no
  // stated reason. Its own error, like `healthError` above.
  const [accountsError, setAccountsError] = useState(null);

  // The AWS axis, kept in its own state WITH ITS OWN ERROR. "No health row for
  // this account" and "the health feed could not be read" are different answers
  // and must not collapse into one another.
  const [health, setHealth] = useState([]);
  const [healthError, setHealthError] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthTruncated, setHealthTruncated] = useState(false);

  const [invitations, setInvitations] = useState([]);
  const [invitationsError, setInvitationsError] = useState(null);

  const [expanded, setExpanded] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());
  const [confirmRemove, setConfirmRemove] = useState(null);

  const pollRef = useRef(null);
  const pollFailuresRef = useRef(0);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      // The grid is bounded by accounts × Regions and is meant to be read whole:
      // a truncated grid would answer "why is this account missing?" with the
      // wrong answer. The cursor is followed to exhaustion, and the bound below
      // is REPORTED rather than applied silently.
      //
      // This route answers the whole grid in one response with `nextCursor: ""`,
      // so today the loop makes exactly one pass and `truncated` stays false — no
      // banner is raised for a single-page answer. The cap and its warning STAY.
      // They cost one comparison, and deleting a guard because the current server
      // never trips it is how a truncation warning goes missing later.
      const all = [];
      let cursor = '';
      let pages = 0;
      let truncated = false;
      do {
        // eslint-disable-next-line no-await-in-loop
        const res = await listIpamDiscoveredAccounts(cursor ? { cursor } : undefined);
        const page = inventoryPage(res?.data);
        all.push(...page.items);
        cursor = page.nextCursor;
        pages += 1;
        if (cursor && pages >= 25) { truncated = true; break; }
      } while (cursor);
      setHealth(all);
      setHealthTruncated(truncated);
      setHealthError(null);
    } catch (e) {
      setHealth([]);
      setHealthTruncated(false);
      setHealthError(e.message);
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const loadInvitations = useCallback(async () => {
    try {
      const res = await listIpamShareInvitations();
      setInvitations(res?.data || []);
      setInvitationsError(null);
    } catch (e) {
      setInvitations([]);
      setInvitationsError(e.message);
    }
  }, []);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const [c, m] = await Promise.all([getIpamConfig(), listIpamMembers()]);
      const rows = m?.data || [];
      setConfig(c?.data || null);
      setMembers(rows);
      // A poll can enrol an account still ticked in the picker below. Re-sending
      // an enrolled account is skipped server-side, and if EVERY tick is already
      // enrolled the whole request is refused — which reads as a failure of an
      // Add that in fact succeeded.
      const live = new Set(rows.filter((r) => r.phase !== 'disabled').map((r) => r.accountId));
      setSelected((prev) => ([...prev].some((id) => live.has(id))
        ? new Set([...prev].filter((id) => !live.has(id)))
        : prev));
      setConfigRead('ok');
      setError(null);
    } catch (e) {
      // Conservative: the two reads share one try, so a members failure also
      // marks the config unread. "We do not know" costs an offered click the
      // service will answer; the other direction is a permanently dead button.
      setConfigRead('failed');
      setError(e.message);
    } finally {
      if (showSpinner) setLoading(false);
    }
    // Best-effort FOR THE LABELS, and reported for the CANDIDATES. It still never
    // blanks the page — names degrade to bare ids — but the picker below cannot
    // claim there is nothing left to enrol when the list of candidates is the
    // read that failed.
    try {
      const a = await listSecurityHubAccounts();
      setAccounts(a?.data?.accounts || []);
      setAccountsError(null);
    } catch (e) {
      setAccounts([]);
      setAccountsError(e?.message || 'Could not read the linked accounts for this organization.');
    }
  }, []);

  useEffect(() => { load(); loadHealth(); loadInvitations(); }, [load, loadHealth, loadInvitations]);

  // Poll while EITHER a cell is mid-flight OR the config is mid-operation. The
  // config half is not redundant: an update claims the config before it touches a
  // single cell, so keying only on cell phases leaves the bar frozen for the whole
  // opening stretch of every operation.
  //
  // Do NOT clearInterval in this effect's cleanup: the tick calls setMembers with
  // a new array on every pass, so [members] changes, React fires the cleanup and
  // the interval is destroyed and recreated every tick — which is exactly what
  // freezes config.progress in the rendered bar. The interval stops itself, and
  // the unmount effect below handles teardown.
  //
  // AND IT HAS A FAILURE CEILING. Three consecutive failures stop it and say so:
  // a poll that swallows every rejection forever draws a bar that never advances
  // and never errors over a backend that has stopped answering, which is the one
  // state an operator cannot distinguish from a slow enrol. The counter resets on
  // every success and whenever a fresh interval is armed, so failures tolerated
  // during an earlier operation never count against this one.
  useEffect(() => {
    const active = config?.status === 'in_progress'
      || members.some((m) => ACTIVE_PHASES.includes(m.phase));
    if (active && !pollRef.current) {
      pollFailuresRef.current = 0;
      pollRef.current = setInterval(async () => {
        try {
          const [m, c] = await Promise.all([listIpamMembers(), getIpamConfig()]);
          pollFailuresRef.current = 0;
          const next = m?.data || [];
          const nextCfg = c?.data || null;
          setMembers(next);
          setConfig(nextCfg);
          const stillActive = nextCfg?.status === 'in_progress'
            || next.some((r) => ACTIVE_PHASES.includes(r.phase));
          if (!stillActive && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch (_) {
          // Silent below the ceiling — one blipped poll must not replace a
          // multi-minute enrollment narration with a red card.
          pollFailuresRef.current += 1;
          if (pollFailuresRef.current >= MAX_POLL_FAILURES) {
            if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
            setError(POLL_STALLED_MESSAGE);
          }
        }
      }, POLL_INTERVAL_MS);
    }
  }, [members, config?.status]);

  useEffect(() => () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const nameById = useMemo(() => {
    const map = {};
    accounts.forEach((a) => { if (a.accountId) map[a.accountId] = a.name || a.accountName || ''; });
    return map;
  }, [accounts]);

  // An account with no name renders its id ONCE.
  const acctLabel = useCallback(
    (id) => (nameById[id] && nameById[id] !== id ? `${nameById[id]} (${id})` : id),
    [nameById],
  );

  // AWS's rows, keyed by account. An account can carry several because AWS reports
  // once per Region its discovery covers.
  const healthByAccount = useMemo(() => {
    const map = new Map();
    health.forEach((h) => {
      const list = map.get(h.accountId) || [];
      list.push(h);
      map.set(h.accountId, list);
    });
    map.forEach((list) => list.sort(
      (a, b) => String(a.discoveryRegion).localeCompare(String(b.discoveryRegion)),
    ));
    return map;
  }, [health]);

  // The fused row. One per account, carrying BOTH axes and the per-Region lines
  // the expander reveals.
  const rows = useMemo(() => members.map((m) => {
    const cells = healthByAccount.get(m.accountId) || [];
    const regions = [...new Set([
      ...(m.discoveryRegion ? [m.discoveryRegion] : []),
      ...cells.map((c) => c.discoveryRegion).filter(Boolean),
    ])].sort((a, b) => String(a).localeCompare(String(b)));
    const byRegion = new Map(cells.map((c) => [c.discoveryRegion, c]));
    return {
      ...m,
      lines: regions.map((region) => ({ region, aws: byRegion.get(region) || null })),
      awsCells: cells,
    };
  }), [members, healthByAccount]);

  // Every row that is not a tombstone. THIS IS THE GRID AND THE PICKER, and it is
  // deliberately NOT the coverage filter: a `removing` account has to keep its
  // row — the Removing chip and its spinner are the only feedback an operator
  // gets while the teardown runs — and has to keep its id out of `notEnrolled`,
  // or a removal in flight offers itself straight back for re-adding.
  const liveRows = useMemo(() => rows.filter((r) => r.phase !== 'disabled'), [rows]);

  // The COVERAGE set — the shared filter, and what every count on this page is
  // actually about: how many accounts IPAM should be discovering, and how many of
  // those it has stopped discovering. An account mid-removal is neither, so it is
  // reported separately below rather than folded into "enrolled".
  const coveredRows = useMemo(() => liveRows.filter(isCoveredMember), [liveRows]);
  const removingRows = useMemo(
    () => liveRows.filter((r) => r.phase === 'removing'),
    [liveRows],
  );

  const removedIds = useMemo(
    () => new Set(rows.filter((r) => r.phase === 'disabled').map((r) => r.accountId)),
    [rows],
  );

  const liveIds = useMemo(() => new Set(liveRows.map((r) => r.accountId)), [liveRows]);
  // The host is never a member of the picker: HostAccountID is a separate wizard
  // field and is immutable, so offering it here would produce a guaranteed refusal.
  const notEnrolled = useMemo(
    () => accounts.filter((a) => !liveIds.has(a.accountId) && a.accountId !== config?.hostAccountId),
    [accounts, liveIds, config?.hostAccountId],
  );

  // Over the COVERED rows, not merely the untombstoned ones. A `not-found` status
  // survives a removal — the tombstone write touches phase and error_message
  // only, and the lifecycle sync skips `phase = disabled` cells — so an account
  // removed on this page's own advice ("Remove and re-add each account to rebuild
  // it") would otherwise be named by this banner forever.
  const brokenRows = useMemo(
    () => coveredRows.filter((r) => r.resourceDiscoveryStatus === 'not-found'),
    [coveredRows],
  );

  const invitationRows = useMemo(
    () => liveRows.filter((r) => r.shareAcceptMode === 'invitation'),
    [liveRows],
  );

  const failedRows = useMemo(() => liveRows.filter((r) => r.phase === 'failed'), [liveRows]);

  // RULE 2. Nil across EVERY row means AWS has never completed a sweep anywhere —
  // which is a completely different diagnosis from "the sweep ran and found
  // nothing", and has the opposite next action.
  const noSweepEverCompleted = health.length > 0
    && health.every((h) => !h.lastSuccessfulDiscoveryTime);

  const failureRows = useMemo(() => health.filter((h) => h.failureCode), [health]);

  const operatingRegions = useMemo(
    () => toStringArray(config?.operatingRegions),
    [config?.operatingRegions],
  );

  const toggleExpanded = (accountId) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(accountId)) next.delete(accountId); else next.add(accountId);
    return next;
  });

  const jumpToInvitations = () => {
    const el = document.getElementById(INVITATIONS_ANCHOR);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const run = async (fn) => {
    setBusy(true);
    let failure = null;
    try {
      await fn();
    } catch (e) {
      failure = e.message;
    }
    // Every mutation answers {"status":"in_progress"} and dispatches the real work
    // asynchronously, so the response says nothing about the outcome. Re-read the
    // members list, which is the only source of a row's true state.
    await load(false).catch(() => {});
    await loadHealth().catch(() => {});
    // AFTER the reload, never before: a successful load() clears `error`, so a
    // failure set first would be wiped milliseconds later and a rejected mutation
    // would look like it went through.
    setError(failure);
    setBusy(false);
  };

  const onAdd = () => run(async () => {
    await addIpamMembers([...selected]);
    setSelected(new Set());
  });

  // Deliberately one account at a time, with no "retry all": RetryMember claims
  // the config before it returns, so a loop would retry the first and report a
  // spurious error for the rest.
  const onRetry = (accountId) => run(() => retryIpamMember(accountId));

  const onRemove = (accountId) => {
    setConfirmRemove(null);
    return run(() => removeIpamMember(accountId));
  };

  const onAccept = (invitationArn) => run(async () => {
    await acceptIpamShareInvitation(invitationArn);
    await loadInvitations();
  });

  const onRefreshDiscovery = () => run(async () => {
    await startIpamRefresh('discovery');
    await loadInvitations();
  });

  const discoveryGate = discoveryRefreshGate(config, configRead);

  const header = (
    <PortalPageHeader
      icon={<GroupsIcon sx={{ fontSize: 24 }} />}
      title="Accounts"
      subtitle="Prism's enrollment and AWS's own discovery health, on one row per account"
      color={IPAM_COLOR}
      action={(
        <Tooltip title={busy ? IPAM_REFRESH_RUNNING_REASON : discoveryGate.reason}>
          {/* A disabled MUI Button emits no pointer events, so the Tooltip needs
              a wrapper to hang off or the reason never reaches the screen. */}
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              disabled={busy || discoveryGate.disabled}
              onClick={onRefreshDiscovery}
              sx={{ fontFamily: FONT_BODY, textTransform: 'none' }}
            >
              {busy ? 'Working…' : 'Re-check discovery'}
            </Button>
          </span>
        </Tooltip>
      )}
    />
  );

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading accounts…" color={IPAM_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // MUST precede the not-configured branch: a rejection from GET /config leaves
  // `config` null, so without this a 500 renders as "IPAM is not set up yet" and
  // points an operator whose IPAM is running and billing at the setup wizard.
  //
  // Keyed on the READ OUTCOME as well as on `error`: the old guard held only
  // while every rejection path produces a truthy message, and the branch that
  // inherits the case where one does not is a claim about the customer's setup.
  if ((error || configRead === 'failed') && !config) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => { load(); loadHealth(); }} />
        </motion.div>
      </motion.div>
    );
  }

  // NOT SET UP IS A CLAIM ABOUT AN IPAM, AND `disabled` IS NOT EVIDENCE FOR IT.
  //
  // A clean teardown of an ADOPTED IPAM settles `disabled` over an IPAM that is
  // DELIBERATELY NEVER DELETED and goes on metering — the backend's own pre-flight
  // says "The IPAM is STILL RUNNING AND STILL BILLING … Delete the IPAM yourself in
  // the host account to stop it" for the same tenant this branch was telling to go
  // and complete the setup wizard. ipamResidualIpamAlert answers null whenever
  // Prism holds no such evidence, and the original sentence stands.
  if (!config || config.status === 'disabled') {
    const residual = ipamResidualIpamAlert(config, 'enrolled accounts');
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert
            severity={residual ? 'error' : 'info'}
            action={(
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/ipam')}>
                Go to setup
              </Button>
            )}
          >
            {residual || ('Amazon VPC IPAM is not set up yet for this customer. Complete the setup '
              + 'wizard first to enrol accounts.')}
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  const canMutate = configAllowsMemberOps(config.status);
  const setupFailed = config.status === 'failed';

  // THE SERVER'S PER-CACHE FRESHNESS STAMP, READ HERE FOR THE FIRST TIME.
  // models.IpamConfig.DiscoveredAccountsReadAt is set only by a run in which
  // EVERY read feeding the discovered-account cache completed, and it is omitted
  // from the payload while nil — so absent means "Prism has never read this cache
  // whole", which is a third value and not an empty AWS answer. It was written
  // every refresh and consumed by nobody, which is why an unread cache rendered
  // as "AWS: not reported yet" and a last sweep of "Never".
  const discoveredAccountsRead = !!config.discoveredAccountsReadAt;
  const operationRunning = config.status === 'in_progress'
    || members.some((m) => ACTIVE_PHASES.includes(m.phase));

  const showInvitationsSection = invitations.length > 0 || invitationRows.length > 0 || !!invitationsError;

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{header}</motion.div>

      {error && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
          <PortalSectionHeader
            icon={<InfoOutlinedIcon sx={{ fontSize: 20 }} />}
            title="Configuration"
            subtitle="The IPAM host is chosen once and cannot be moved — moving it would mean deleting the IPAM and all its address history"
            color={IPAM_COLOR}
          />
          <ConfigRow label="Host account" value={acctLabel(config.hostAccountId)} />
          <ConfigRow label="Home region" value={config.homeRegion || '—'} />
          <ConfigRow label="Operating regions" value={operatingRegions.join(', ') || '—'} />
          <ConfigRow label="Tier" value={formatStatusLabel(config.tier) || '—'} />
          <ConfigRow label="Status" value={formatStatusLabel(config.status)} />
          {/* THE SUBJECT OF THIS ABSENCE IS PRISM, NOT AWS.
              `lastDiscoveryRefreshAt` is PRISM'S OWN refresh clock — the sibling
              cold-start panel renders the very same field as "Prism last read
              AWS" — so "AWS has not reported a completed sweep yet" pointed every
              operator who read it at the wrong service. */}
          <ConfigRow
            label="Last discovery refresh"
            value={config.lastDiscoveryRefreshAt
              ? `${absoluteTime(config.lastDiscoveryRefreshAt)} (${relativeTime(config.lastDiscoveryRefreshAt)})`
              : IPAM_TENANT_NEVER_REFRESHED}
          />
        </Paper>
      </motion.div>

      {setupFailed && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={(
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/ipam')}>
                Re-run setup
              </Button>
            )}
          >
            VPC IPAM setup failed{config.errorMessage ? `: ${config.errorMessage}` : '.'}
            {' '}
            Nothing is running and nothing will resume on its own — enrollment changes
            stay unavailable until setup is re-run. The accounts below show how far
            each one got.
          </Alert>
        </motion.div>
      )}

      {config.status === 'partial_failure' && failedRows.length > 0 && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            {failedRows.length} {plural(failedRows.length, 'account', 'accounts')} did not
            finish enrolling. Every other account is enrolled normally — use <b>Retry</b> on
            the rows below.
          </Alert>
        </motion.div>
      )}

      {/* The discovery health feed itself being UNREADABLE is its own state, and
          it is drawn ABOVE the grid. Without this, an account with no health row
          because the read failed renders identically to an account AWS has simply
          not reached yet — a confident negative built out of missing data. */}
      {healthError && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              AWS discovery health could not be read.
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
              The discovery column below is blank because Prism could not read
              GetIpamDiscoveredAccounts, <b>not</b> because AWS reported nothing for these
              accounts. Prism&apos;s own enrollment phases are still accurate. ({healthError})
            </Typography>
          </Alert>
        </motion.div>
      )}

      {/* The discovery cache having NEVER BEEN READ is its own state, distinct
          from the feed rejecting (above) and from AWS reporting nothing (below).
          Drawn once at the top rather than inferred from a grid full of neutral
          chips. */}
      {!healthError && !discoveredAccountsRead && (
        <motion.div variants={sectionVariants}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              Prism has not read AWS&apos;s discovered-account list yet.
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
              {IPAM_ACCOUNT_DISCOVERY_UNREAD_HINT}
            </Typography>
          </Alert>
        </motion.div>
      )}

      {healthTruncated && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            The discovery-health grid was cut short after 25 pages, so some accounts below
            show no AWS row even though one exists. Treat a blank discovery column as
            unknown rather than as a finding.
          </Alert>
        </motion.div>
      )}

      {/* RULE 3 — silent breakage, always drawn as a warning ABOVE the results. */}
      {brokenRows.length > 0 && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              {brokenRows.length} of {coveredRows.length}{' '}
              {plural(coveredRows.length, 'account is', 'accounts are')} no longer being discovered.
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
              Their resource discovery was deleted, or their RAM share to the host was revoked.
              Everything in those accounts is missing from every other table in this capability,
              however clean those tables look, and <b>no amount of waiting will change that</b> —
              the association still consumes one of the IPAM&apos;s association slots while nothing
              is discovered. Remove and re-add each account to rebuild it.
            </Typography>
          </Alert>
        </motion.div>
      )}

      {/* RULE 2 — "AWS has never completed a sweep" must never render as "nothing
          was found". Opposite diagnoses, opposite next actions. */}
      {noSweepEverCompleted && !healthError && (
        <motion.div variants={sectionVariants}>
          <Alert severity="info" icon={<TravelExploreIcon />} sx={{ mb: 3 }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              AWS has never completed a discovery sweep for any of these accounts.
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
              Every account AWS reported is missing a last-successful-discovery time. That is
              <b> not</b> the same as &ldquo;nothing was found&rdquo; — nothing has been looked at
              yet. The first sweep takes up to about 20 minutes after enabling and a newly added
              account typically appears within 4&ndash;7 minutes, so the inventory tables being
              empty right now is expected and there is nothing to retry.
            </Typography>
          </Alert>
        </motion.div>
      )}

      {failureRows.length > 0 && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              AWS reported a discovery failure on {failureRows.length}{' '}
              {plural(failureRows.length, 'account/region pair', 'account/region pairs')}.
            </AlertTitle>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {[...new Set(failureRows.map((h) => h.failureCode))].map((code) => {
                const meta = failureCodeMeta(code);
                const n = failureRows.filter((h) => h.failureCode === code).length;
                return (
                  <Tooltip key={code} title={meta.hint}>
                    <Chip size="small" label={`${n} × ${meta.label}`} sx={statusChipSx(meta.intent)} />
                  </Tooltip>
                );
              })}
            </Stack>
          </Alert>
        </motion.div>
      )}

      {operationRunning && (
        <motion.div variants={sectionVariants}>
          <OperationProgress
            percent={config.progress}
            label={config.progressLabel || 'Working…'}
            sx={{ mb: 3 }}
          />
        </motion.div>
      )}

      {/* === The fused grid === */}
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
          <PortalSectionHeader
            icon={<GroupsIcon sx={{ fontSize: 20 }} />}
            title="Enrolled accounts"
            subtitle="Prism's phase says the enrollment finished. AWS's discovery says the sweep is actually running. Expand a row to see both, per Region."
            color={IPAM_COLOR}
            action={(
              // "Enrolled" counts the COVERED rows, so this chip agrees with the
              // same sentence on the Dashboard, Address Space, Allocations and
              // Public IPs. Accounts on their way out are counted beside it
              // rather than inside it — they still have a row in the table below,
              // and a chip that silently disagreed with a visible row count is
              // the disagreement this filter exists to remove.
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={`${coveredRows.length} enrolled`} sx={statusChipSx('info')} />
                {removingRows.length > 0 && (
                  <Tooltip title="These accounts are being un-enrolled right now. They still have a row below until Prism finishes disassociating, un-sharing and deleting their resource discovery.">
                    <Chip size="small" label={`${removingRows.length} removing`} sx={statusChipSx('warning')} />
                  </Tooltip>
                )}
              </Stack>
            )}
          />

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={{ ...headCellSx, width: 48 }} />
                  <TableCell sx={headCellSx}>Account</TableCell>
                  <TableCell sx={headCellSx}>Prism enrollment</TableCell>
                  <TableCell sx={headCellSx}>AWS discovery</TableCell>
                  <TableCell sx={headCellSx}>Last successful sweep</TableCell>
                  <TableCell align="right" sx={headCellSx}>Retries</TableCell>
                  <TableCell align="right" sx={headCellSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {liveRows.map((row, index) => {
                  const meta = phaseMeta(row.phase);
                  const discovery = discoveryStatusMeta(row.resourceDiscoveryStatus);
                  const refusal = retryRefusal(row);
                  const open = expanded.has(row.accountId);
                  const failures = row.awsCells.filter((c) => c.failureCode);
                  const sweeps = row.awsCells
                    .map((c) => c.lastSuccessfulDiscoveryTime)
                    .filter(Boolean)
                    .sort();
                  const latestSweep = sweeps.length > 0 ? sweeps[sweeps.length - 1] : null;
                  return (
                    <Fragment key={row.accountId}>
                      <TableRow hover sx={rowSx(index)}>
                        <TableCell sx={bodyCellSx}>
                          <IconButton size="small" onClick={() => toggleExpanded(row.accountId)}>
                            {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Box component="span" sx={{ fontWeight: 600, color: CK.navy }}>
                              {acctLabel(row.accountId)}
                            </Box>
                            {row.role === 'host' && (
                              <Tooltip title="This account hosts the IPAM itself. It is discovered by the IPAM's own default resource discovery, has no discovery of its own, and cannot be removed here — removing it means deleting the IPAM.">
                                <Chip size="small" label="Host" sx={statusChipSx('purple')} />
                              </Tooltip>
                            )}
                            {row.adopted && (
                              <Tooltip title="This account's resource discovery already existed when Prism enrolled it. Prism uses it but will never delete it.">
                                <Chip size="small" icon={<HandshakeOutlinedIcon />} label="Adopted" sx={statusChipSx('success')} />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title={meta.hint}>
                              <Chip size="small" label={meta.label} sx={statusChipSx(meta.intent)} />
                            </Tooltip>
                            {ACTIVE_PHASES.includes(row.phase) && (
                              <CircularProgress size={14} thickness={5} sx={{ color: IPAM_COLOR }} />
                            )}
                          </Stack>
                          {row.errorMessage && (
                            <Typography
                              sx={{
                                display: 'block', mt: 0.5, fontFamily: FONT_BODY,
                                fontSize: '0.75rem', color: CK.red, wordBreak: 'break-word',
                              }}
                            >
                              {row.errorMessage}
                            </Typography>
                          )}
                          {row.shareAcceptMode === 'invitation' && (
                            <Button
                              size="small"
                              onClick={jumpToInvitations}
                              sx={{
                                mt: 0.5, p: 0, minWidth: 0, fontFamily: FONT_BODY,
                                fontSize: '0.75rem', fontWeight: 600, textTransform: 'none',
                              }}
                            >
                              This account&apos;s share needs an invitation — open the repair list
                            </Button>
                          )}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                            {discovery ? (
                              <Tooltip title={discovery.hint}>
                                <Chip size="small" label={discovery.label} sx={statusChipSx(discovery.intent)} />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Prism has not recorded a resource-discovery status for this account yet.">
                                <Chip size="small" label="Not recorded" sx={statusChipSx('neutral')} />
                              </Tooltip>
                            )}
                            {/* THREE WAYS THIS COLUMN CAN BE BLANK, AND ONLY ONE
                                OF THEM IS ABOUT AWS. `healthError` is THIS
                                BROWSER's fetch failing. `discoveredAccountsRead`
                                false is the server saying no refresh has ever
                                completed the discovered-account read — a
                                successful, empty response from a cache nothing
                                ever filled, which used to render as "AWS: not
                                reported yet". Only past both is an absent row
                                AWS's own answer. */}
                            {/* eslint-disable-next-line no-nested-ternary */}
                            {healthError ? (
                              <Tooltip title="AWS's discovery health could not be read — see the warning above. This is unknown, not healthy.">
                                <Chip size="small" label="AWS: unknown" sx={statusChipSx('neutral')} />
                              </Tooltip>
                            ) : !discoveredAccountsRead ? (
                              <Tooltip title={IPAM_ACCOUNT_DISCOVERY_UNREAD_HINT}>
                                <Chip
                                  size="small"
                                  label={IPAM_ACCOUNT_DISCOVERY_UNREAD_LABEL}
                                  sx={statusChipSx('neutral')}
                                />
                              </Tooltip>
                            ) : failures.length > 0 ? (
                              failures.map((c) => {
                                const fm = failureCodeMeta(c.failureCode);
                                return (
                                  <Tooltip key={`${c.discoveryRegion}:${c.failureCode}`} title={`${c.discoveryRegion}: ${fm.hint}${c.failureMessage ? ` AWS said: ${c.failureMessage}` : ''}`}>
                                    <Chip size="small" label={`AWS: ${fm.label}`} sx={statusChipSx(fm.intent)} />
                                  </Tooltip>
                                );
                              })
                            ) : row.awsCells.length === 0 ? (
                              // Presence in GetIpamDiscoveredAccounts is the exact
                              // readiness predicate — measured at ~4.5 minutes past
                              // associate-complete. Absence is "not yet usable",
                              // never "healthy" and never "empty".
                              <Tooltip title="AWS has not reported this account in GetIpamDiscoveredAccounts yet. Enrollment finishing is NOT the same as the account being usable: an account becomes usable only once the discovery sweep has actually run, typically 4-7 minutes later. Until then nothing in it appears anywhere.">
                                <Chip size="small" label="AWS: not reported yet" sx={statusChipSx('info')} />
                              </Tooltip>
                            ) : (
                              // RULE 1: absence of a failure code IS the healthy
                              // signal. There is no positive "ok" value to read.
                              <Tooltip title="AWS reported no failure code for this account, which is the only healthy signal AWS gives — there is no positive value to look for.">
                                <Chip size="small" label="AWS: no failures" sx={statusChipSx('success')} />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {/* eslint-disable-next-line no-nested-ternary */}
                          {healthError ? (
                            <Tooltip title="Unknown — the health feed could not be read.">
                              <Box component="span" sx={{ color: CK.disabled, cursor: 'help' }}>—</Box>
                            </Tooltip>
                          ) : !discoveredAccountsRead ? (
                            // "Never", in amber, with "AWS has never completed a
                            // discovery sweep for this account" behind it — over a
                            // cache Prism has never read whole. The sentence was
                            // right and the SUBJECT was wrong.
                            <Tooltip title={IPAM_ACCOUNT_SWEEP_UNREAD_HINT}>
                              <Box component="span" sx={{ color: CK.disabled, cursor: 'help' }}>—</Box>
                            </Tooltip>
                          ) : latestSweep ? (
                            <Tooltip title={absoluteTime(latestSweep)}>
                              <Box component="span" sx={{ cursor: 'help' }}>{relativeTime(latestSweep)}</Box>
                            </Tooltip>
                          ) : (
                            <Tooltip title="AWS has never completed a discovery sweep for this account. That is not “nothing was found” — nothing has been looked at yet.">
                              <Box component="span" sx={{ color: CK.amber, cursor: 'help', fontWeight: 600 }}>
                                Never
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={bodyCellSx}>
                          {row.retryCount > 0 ? row.retryCount : '-'}
                        </TableCell>
                        <TableCell align="right" sx={bodyCellSx}>
                          {/* Retry is offered only for `failed`. `remove_failed`
                              deliberately gets none: re-enrolling re-attaches the
                              grant the operator asked Prism to remove. */}
                          {row.phase === 'failed' && (
                            refusal ? (
                              <Tooltip title={`Retrying will not help. ${refusal}`}>
                                <Box component="span" sx={{ mr: 1 }}>
                                  <Button
                                    size="small"
                                    color="warning"
                                    disabled
                                    sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
                                  >
                                    Retry
                                  </Button>
                                </Box>
                              </Tooltip>
                            ) : (
                              <Button
                                size="small"
                                color="warning"
                                disabled={busy || !canMutate}
                                onClick={() => onRetry(row.accountId)}
                                sx={{ mr: 1, fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
                              >
                                Retry
                              </Button>
                            )
                          )}
                          {row.role !== 'host' && row.phase !== 'removing' && (
                            <Button
                              size="small"
                              color="error"
                              disabled={busy || !canMutate}
                              onClick={() => setConfirmRemove(row.accountId)}
                              sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
                            >
                              Remove
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* One line per (account, Region), carrying BOTH axes. */}
                      <TableRow>
                        <TableCell sx={{ py: 0, borderBottom: open ? undefined : 'none' }} colSpan={7}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ ...insetPanelSx, my: 1.5 }}>
                              {row.lines.length === 0 ? (
                                <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.muted }}>
                                  Prism has not recorded a discovery Region for this account yet, and AWS
                                  has not reported one. Nothing here is a finding — the account has not
                                  reached the sweep.
                                </Typography>
                              ) : (
                                <Stack spacing={1}>
                                  {row.lines.map((line) => {
                                    const aws = line.aws;
                                    const fm = aws?.failureCode ? failureCodeMeta(aws.failureCode) : null;
                                    return (
                                      <Box
                                        key={line.region}
                                        sx={{
                                          display: 'flex', flexWrap: 'wrap', gap: 0.75,
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Box component="span" sx={{ ...cidrCellSx, border: 0, p: 0, minWidth: 130 }}>
                                          {line.region}
                                        </Box>
                                        <Tooltip title={`Prism's enrollment phase is recorded per ACCOUNT, not per Region — one resource discovery covers every Region the IPAM operates in. ${meta.hint}`}>
                                          <Chip size="small" label={`Prism: ${meta.label}`} sx={statusChipSx(meta.intent)} />
                                        </Tooltip>
                                        {line.region === row.discoveryRegion && discovery && (
                                          <Tooltip title={discovery.hint}>
                                            <Chip size="small" label={discovery.label} sx={statusChipSx(discovery.intent)} />
                                          </Tooltip>
                                        )}
                                        {healthError ? (
                                          <Chip size="small" label="AWS: unknown" sx={statusChipSx('neutral')} />
                                        ) : !aws ? (
                                          <Tooltip title="AWS has not reported this account in this Region. Until it does, the account is enrolled but not yet usable — it will not appear in any inventory table.">
                                            <Chip size="small" label="AWS: not reported yet" sx={statusChipSx('info')} />
                                          </Tooltip>
                                        ) : fm ? (
                                          <Tooltip title={`${fm.hint}${aws.failureMessage ? ` AWS said: ${aws.failureMessage}` : ''}`}>
                                            <Chip size="small" label={`AWS: ${fm.label}`} sx={statusChipSx(fm.intent)} />
                                          </Tooltip>
                                        ) : (
                                          <Tooltip title="AWS reported no failure code for this account and Region, which is the only healthy signal it gives.">
                                            <Chip size="small" label="AWS: no failures" sx={statusChipSx('success')} />
                                          </Tooltip>
                                        )}
                                        {aws && (
                                          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.body }}>
                                            {aws.lastSuccessfulDiscoveryTime
                                              ? `last successful sweep ${relativeTime(aws.lastSuccessfulDiscoveryTime)}`
                                              : 'no sweep has ever completed here'}
                                            {aws.lastAttemptedDiscoveryTime
                                              ? ` · last attempt ${relativeTime(aws.lastAttemptedDiscoveryTime)}`
                                              : ''}
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  })}
                                  {/* AWS's raw lifecycle states, drawn with the
                                      SHARED chip so an isolate-* value — "the
                                      owning account left the organization" —
                                      cannot read as ordinary progress here while
                                      reading as a departure on the pool screens.
                                      An absent state is a muted em-dash, never a
                                      chip. */}
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted }}>
                                      Discovery state
                                    </Typography>
                                    <IpamStateChip state={row.discoveryState} />
                                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted }}>
                                      Association state
                                    </Typography>
                                    <IpamStateChip state={row.associationState} />
                                  </Stack>
                                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                    {row.resourceDiscoveryId && (
                                      <Typography sx={{ ...cidrCellSx, border: 0, p: 0, fontSize: '0.75rem', color: CK.muted }}>
                                        discovery {row.resourceDiscoveryId}
                                      </Typography>
                                    )}
                                    {row.associationId && (
                                      <Typography sx={{ ...cidrCellSx, border: 0, p: 0, fontSize: '0.75rem', color: CK.muted }}>
                                        association {row.associationId}
                                      </Typography>
                                    )}
                                    {row.shareAcceptMode && (
                                      <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted }}>
                                        RAM share accepted via {row.shareAcceptMode}
                                      </Typography>
                                    )}
                                  </Stack>
                                </Stack>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}

                {liveRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <PortalEmptyState
                        icon={<GroupsIcon sx={{ fontSize: 28 }} />}
                        title="No accounts enrolled yet"
                        description="Pick accounts from the list below and add them. Each one gets its own resource discovery, shared to the IPAM host."
                        color={IPAM_COLOR}
                        py={2}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {healthLoading && (
            <Typography sx={{ mt: 1.5, fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted }}>
              Reading AWS discovery health…
            </Typography>
          )}
          {/* The rule that licenses reading a blank cell as healthy. It is only
              true once the cache HAS been read — before that a blank cell is
              Prism's absence, not AWS's. */}
          {!healthLoading && !healthError && discoveredAccountsRead && liveRows.length > 0 && (
            <Typography sx={{ mt: 1.5, fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted }}>
              <b>AWS reports health by ABSENCE:</b> there is no &ldquo;healthy&rdquo; value to read, so
              an account with no failure code is a working account. An account AWS has not reported at
              all is a different thing again — it is enrolled but not yet usable, and nothing in it
              appears in any inventory table until its first sweep lands.
            </Typography>
          )}
        </Paper>
      </motion.div>

      {/* === Repair: pending RAM share invitations === */}
      {showInvitationsSection && (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }} id={INVITATIONS_ANCHOR}>
            <PortalSectionHeader
              icon={<HandshakeOutlinedIcon sx={{ fontSize: 20 }} />}
              title="Share invitations"
              subtitle="In organizations without RAM organization sharing, a member's discovery reaches the host as an invitation the host must accept"
              color={IPAM_COLOR}
              action={(
                <Chip
                  size="small"
                  label={`${invitations.filter((i) => String(i.status).toUpperCase() === 'PENDING').length} pending`}
                  sx={statusChipSx(invitations.some((i) => String(i.status).toUpperCase() === 'PENDING') ? 'warning' : 'neutral')}
                />
              )}
            />
            {invitationsError && <PortalErrorState error={invitationsError} onRetry={loadInvitations} sx={{ mb: 2 }} />}
            {invitationRows.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {invitationRows.length} enrolled {plural(invitationRows.length, 'account', 'accounts')}{' '}
                ({invitationRows.map((r) => r.accountId).join(', ')}) reached the host through the
                invitation branch rather than automatic organization sharing. If one of them is stuck
                before <b>Associating</b>, an unaccepted or expired invitation is the usual cause.
              </Alert>
            )}
            {invitations.length === 0 && !invitationsError ? (
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
                RAM reports no share invitations addressed to the IPAM host. On this branch that is the
                normal steady state once every share has been accepted.
              </Typography>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow sx={tableHeadRowSx}>
                      <TableCell sx={headCellSx}>Share</TableCell>
                      <TableCell sx={headCellSx}>From account</TableCell>
                      <TableCell sx={headCellSx}>Status</TableCell>
                      <TableCell sx={headCellSx}>Raised</TableCell>
                      <TableCell align="right" sx={headCellSx}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invitations.map((inv, index) => {
                      const meta = invitationStatusMeta(inv.status);
                      const pending = String(inv.status).toUpperCase() === 'PENDING';
                      return (
                        <TableRow key={inv.invitationArn} hover sx={rowSx(index)}>
                          <TableCell sx={bodyCellSx}>{inv.shareName || inv.shareArn}</TableCell>
                          <TableCell sx={cidrCellSx}>{acctLabel(inv.senderAccountId)}</TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Tooltip title={meta.hint}>
                              <Chip size="small" label={meta.label} sx={statusChipSx(meta.intent)} />
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            {relativeTime(inv.invitationTimestamp) || '—'}
                          </TableCell>
                          <TableCell align="right" sx={bodyCellSx}>
                            {pending && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={busy}
                                onClick={() => onAccept(inv.invitationArn)}
                                sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
                              >
                                Accept
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </motion.div>
      )}

      {/* === Not enrolled === */}
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={sectionCardSx}>
          <PortalSectionHeader
            icon={<GroupAddIcon sx={{ fontSize: 20 }} />}
            title="Not enrolled"
            subtitle="Linked accounts whose address space IPAM is not discovering"
            color={IPAM_COLOR}
            action={(
              <Button
                variant="contained"
                disabled={busy || !canMutate || selected.size === 0}
                onClick={onAdd}
                sx={{
                  backgroundColor: IPAM_COLOR, fontFamily: FONT_BODY, fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: IPAM_COLOR_DARK },
                  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
                }}
              >
                {selected.size === 0 ? 'Add to IPAM' : `Add ${selected.size} to IPAM`}
              </Button>
            )}
          />
          {/* WHY IT IS OFF, BRANCHED ON THE STATUS. `configAllowsMemberOps`
              accepts only `enabled` and `partial_failure`, so this Alert carried
              "paused while the current operation finishes" on FOUR statuses — and
              on two of them nothing is running and nothing is going to finish:
              `awaiting_consent` is an abandoned wizard and `blocked` is a teardown
              parked indefinitely on the customer. */}
          {!canMutate && !setupFailed && (
            <Alert
              severity={config.status === 'in_progress' ? 'info' : 'warning'}
              sx={{ mb: 2 }}
            >
              {ipamEnrollmentDisabledReason(config.status)}
            </Alert>
          )}
          {accountsError && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
              <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
                {IPAM_LINKED_ACCOUNTS_UNREADABLE_TITLE}
              </AlertTitle>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
                {IPAM_LINKED_ACCOUNTS_UNREADABLE_BODY} ({accountsError})
              </Typography>
            </Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            Every account added here consumes one of the IPAM&apos;s resource-discovery
            association slots — the default quota is 5 and the host&apos;s own association
            already uses one. Each account&apos;s discovered addresses are also metered:
            the Advanced tier bills per active IP per hour across everything discovered.
          </Alert>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell padding="checkbox">
                    {/* A disabled control that does not say why is the defect.
                        A disabled MUI Checkbox emits no pointer events, so the
                        Tooltip hangs off a wrapper. */}
                    <Tooltip
                      title={notEnrolled.length === 0
                        ? (accountsError
                          ? IPAM_LINKED_ACCOUNTS_UNREADABLE_TITLE
                          : 'Every linked account is already enrolled, so there is nothing to select.')
                        : 'Select every account not yet enrolled'}
                    >
                      <span>
                        <Checkbox
                          indeterminate={notEnrolled.some((a) => selected.has(a.accountId))
                            && !notEnrolled.every((a) => selected.has(a.accountId))}
                          checked={notEnrolled.length > 0 && notEnrolled.every((a) => selected.has(a.accountId))}
                          disabled={notEnrolled.length === 0}
                          onChange={(e) => setSelected(e.target.checked
                            ? new Set(notEnrolled.map((a) => a.accountId))
                            : new Set())}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={headCellSx}>Account</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notEnrolled.map((a, index) => (
                  <TableRow key={a.accountId} hover sx={rowSx(index)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.has(a.accountId)}
                        onChange={(e) => {
                          const n = new Set(selected);
                          if (e.target.checked) n.add(a.accountId); else n.delete(a.accountId);
                          setSelected(n);
                        }}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {acctLabel(a.accountId)}
                      {removedIds.has(a.accountId) && (
                        <Chip label="previously removed" size="small" sx={{ ml: 1, ...statusChipSx('neutral') }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {/* A FAILED READ IS NOT AN ALL-CLEAR. `accounts` is the CANDIDATE
                    source, so an empty `notEnrolled` over a rejected read is
                    "Prism could not look", not "there is nothing to add". */}
                {notEnrolled.length === 0 && accountsError && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <PortalEmptyState
                        icon={<WarningAmberIcon sx={{ fontSize: 28 }} />}
                        title={IPAM_LINKED_ACCOUNTS_UNREADABLE_TITLE}
                        description={IPAM_LINKED_ACCOUNTS_UNREADABLE_BODY}
                        color={IPAM_COLOR}
                        py={2}
                        action={(
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => load(false)}
                            sx={{ fontFamily: FONT_BODY, textTransform: 'none' }}
                          >
                            Retry
                          </Button>
                        )}
                      />
                    </TableCell>
                  </TableRow>
                )}
                {notEnrolled.length === 0 && !accountsError && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <PortalEmptyState
                        icon={<CheckCircleOutlineIcon sx={{ fontSize: 28 }} />}
                        title="Every linked account is enrolled"
                        description="Nothing left to add — new accounts appear here once they join the organization."
                        color={IPAM_COLOR}
                        py={2}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </motion.div>

      {/* Removing one account is cost-bearing IN REVERSE and structurally
          load-bearing, and the dialog says BOTH. Neither half is inferable from
          the other, and an operator who reads only the first will not understand
          why the association count did not drop. */}
      <Dialog open={!!confirmRemove} onClose={() => setConfirmRemove(null)} {...dialogProps} maxWidth="sm">
        <DialogTitle sx={dialogTitleSx}>Remove account from VPC IPAM</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 1 }}>
            Remove <b>{confirmRemove}</b>? Prism disassociates that account&apos;s resource
            discovery from the IPAM, deletes the RAM share the account created, deletes the
            resource discovery itself, and detaches the IAM policy it added to the
            account&apos;s access role. A discovery Prism adopted is left in place.
          </Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 2 }}>
            <b>{IPAM_NO_VPC_DELETED}</b> Every CIDR, subnet and public IP in that account keeps
            working exactly as it does now.
          </Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 2 }}>
            <b>This changes the bill, downwards.</b> The account&apos;s addresses stop being
            discovered, so they stop counting toward the Advanced-tier meter. The IPAM itself
            keeps running and keeps billing for every account that remains.
          </Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 2 }}>
            <b>And it changes what the other pages can tell you.</b> Deleting an account&apos;s
            resource discovery flips the host&apos;s association for it to{' '}
            <b>not-found</b> — so if any step of the removal fails part-way, the association is
            left consuming one of the IPAM&apos;s five association slots while nothing at all is
            being discovered. That account then disappears from every inventory table without
            those tables looking any different.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setConfirmRemove(null)}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() => onRemove(confirmRemove)}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
          >
            Remove account
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
